import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, SxProps } from '@mui/material';

import LoaderQrIcon from '~/assets/icons/svgs/loader-qr.svg?react';
import previewBarBackground from '~/assets/images/iphone-white-bg.webp';
import TestScan from '~/components/builder/TestScan';
import { StyledTab, StyledTabs } from '~/components/ui/StyledTabs';
import { Text } from '~/components/ui/text';
import { PREVIEW_BAR_VIEW_TYPE } from '~/constants/common';
import useBuilderStore from '~/store/useBuilderStore';
import { COLORS } from '~/styles/colors';
import { DynamicQrCodeData, StaticQrCodeData } from '~/types/qrCode';
import { getAreQRCodeStylesChanged } from '~/utils/builder';

interface PreviewBarLayoutProps {
  isQrLoading?: boolean;
  isQrExist?: boolean;
  isDisabledQrCodePreview?: boolean;
  children?: ReactNode;
  qrCodeData?: DynamicQrCodeData | StaticQrCodeData;
  withTabs?: boolean;
  isAnonymous?: boolean;
  sx?: SxProps;
}

export const PreviewBarLayout = ({
  isQrLoading,
  isQrExist,
  isDisabledQrCodePreview = false,
  children,
  qrCodeData,
  withTabs = true,
  isAnonymous = false,
  sx,
}: PreviewBarLayoutProps) => {
  const { t } = useTranslation();

  const [isQrImageLoaded, setIsQrImageLoaded] = useState(false);

  const { activeStep, setPreviewViewType, previewViewType } = useBuilderStore();

  useEffect(() => {
    if (qrCodeData?.qrImage && isQrImageLoaded) {
      setIsQrImageLoaded(false);
    }
  }, [qrCodeData?.qrImage]);

  const isQrCodeVisible = PREVIEW_BAR_VIEW_TYPE.QRCODE === previewViewType && activeStep !== 0;
  const isQRCodeStylesChanged = !!qrCodeData && getAreQRCodeStylesChanged(qrCodeData);

  const showTestScan = isQrExist && activeStep > 0 && !isDisabledQrCodePreview;

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: isAnonymous ? '320px' : '278px',
        maxWidth: isAnonymous ? '320px' : '278px',
        ...sx,
      }}
    >
      <Box sx={{ position: isAnonymous ? 'static' : 'sticky', top: isAnonymous ? 0 : '10px' }}>
        {withTabs && (
          <StyledTabs>
            <StyledTab
              onClick={() => setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.PHONE)}
              $isActive={previewViewType === PREVIEW_BAR_VIEW_TYPE.PHONE}
            >
              {t('buttons:preview')}
            </StyledTab>
            <StyledTab
              disabled={!isQrExist || isDisabledQrCodePreview || activeStep === 0}
              onClick={() => isQrExist && !isDisabledQrCodePreview && setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.QRCODE)}
              $isActive={previewViewType === PREVIEW_BAR_VIEW_TYPE.QRCODE}
            >
              {t('buttons:qrCode')}
            </StyledTab>
          </StyledTabs>
        )}

        <img src={qrCodeData?.qrImage} alt="" style={{ display: 'none' }} onLoad={() => setIsQrImageLoaded(true)} />

        {isQrCodeVisible ? (
          <Box
            sx={{
              backgroundColor: COLORS.white,
              borderRadius: '20px',
              position: 'relative',
              padding: '28px',
              textAlign: 'center',
              boxShadow: '0 0 20px 0 rgba(0, 0, 0, 0.05)',
              minHeight: '282px',
            }}
          >
            {isQrLoading || !isQrImageLoaded ? (
              <LoaderQrIcon />
            ) : (
              <img src={qrCodeData?.qrImage} alt="qr code image" width="100%" />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              backgroundImage: `url(${previewBarBackground})`,
              width: isAnonymous ? '320px' : '278px',
              aspectRatio: '73 / 150',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {isAnonymous && isQrLoading ? (
              <CircularProgress sx={{ margin: '0 auto', color: COLORS.primary }} />
            ) : (
              children
            )}
          </Box>
        )}
        {showTestScan && <TestScan qrImage={qrCodeData?.qrImage} />}

        {(isQrCodeVisible || (isQrExist && activeStep !== 0)) && (
          <>
            {isQRCodeStylesChanged ? (
              <Box
                sx={{
                  border: `1px solid ${COLORS.darkRed}`,
                  marginTop: '12px',
                  padding: '16px',
                  borderRadius: '20px',
                  display: 'flex',
                  gap: '8px',
                  backgroundColor: COLORS.redBg,
                }}
              >
                ℹ️
                <Text variant="input15" sx={{ color: COLORS.grey400 }}>
                  <Text component="span" sx={{ color: COLORS.textMain, textTransform: 'uppercase' }}>
                    {t('general:important')}:{' '}
                  </Text>
                  {t('general:readabilityQr')}
                </Text>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px solid ${COLORS.blue100}`,
                  marginTop: '12px',
                  padding: '16px',
                  borderRadius: '20px',
                  display: 'flex',
                  gap: '8px',
                  backgroundColor: COLORS.blue50,
                }}
              >
                ℹ️
                <Text variant="input15" sx={{ color: COLORS.grey400 }}>
                  <Text component="span" sx={{ color: COLORS.textMain, textTransform: 'uppercase' }}>
                    {t('general:important')}:{' '}
                  </Text>
                  {t('createQrStep1:rememberAboutPreview')}
                </Text>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
